// @ts-strict-ignore
import { Component, OnInit } from "@angular/core";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

@Component({
  selector: C.EWIZ_ADMIN_AGENT_CONFIGURATION,
  templateUrl: "./admin-agent-configuration.component.html",
  standalone: false
})
export class EWizAdminAgentConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck {
  readonly retryHoursAriaLabel = $localize`Retry waiting delay hours`;
  readonly retryMinutesAriaLabel = $localize`Retry waiting delay minutes`;
  readonly retrySecondsAriaLabel = $localize`Retry waiting delay seconds`;

  constructor(private dataService: DataService) {
    super();

    this.transportProtocolTypes = [
      { label: $localize`01 - UDP, UICC in client mode, remote connection`, value: "01" },
      { label: $localize`02 - TCP, UICC in client mode, remote connection`, value: "02" },
      { label: $localize`03 - TCP, UICC in server mode`, value: "03" },
      { label: $localize`04 - UDP, UICC in client mode, local connection`, value: "04" },
      { label: $localize`05 - TCP, UICC in client mode, local connection`, value: "05" },
      { label: $localize`06 - Direct communication channel`, value: "06" }
    ];
  }

  transportProtocolTypes = [];

  ngOnInit() {
    this.profileValues.adminAgent.retryHours = this.toNullableInteger(this.profileValues.adminAgent.retryHours);
    this.profileValues.adminAgent.retryMinutes = this.toNullableInteger(this.profileValues.adminAgent.retryMinutes);
    this.profileValues.adminAgent.retrySeconds = this.toNullableInteger(this.profileValues.adminAgent.retrySeconds);
    this.errorCheck(this.profileValues, this.dataService);
  }

  private toNullableInteger(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsedValue = Number.parseInt(String(value), 10);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    if (!dataService.validHexValue(values.adminAgent.commandDetails[0], 3, 3)) {
      this.errorMessage = $localize`Command Details must be a valid 3 byte HEX value`;
      this.errorFound = true;
    }

    if (!dataService.validHexValue(values.adminAgent.deviceIdentities, 2, 2)) {
      this.errorMessage = $localize`Device Identities must be a valid 2 byte HEX value`;
      this.errorFound = true;
    }

    if (!dataService.validHexValue(values.adminAgent.bearerDescription, 1, 1)) {
      this.errorMessage = $localize`Bearer Description must be a valid 1 byte HEX value`;
      this.errorFound = true;
    }

    if (dataService.isNullOrEmpty(values.adminAgent.bufferSize) || !dataService.valueInRange(values.adminAgent.bufferSize, 1, 65535)) {
      this.errorMessage = $localize`Buffer Size must be between 1 and 65535`;
      this.errorFound = true;
    }

    if (dataService.isNullOrEmpty(values.adminAgent.portNumber) || !dataService.valueInRange(values.adminAgent.portNumber, 1, 65535)) {
      this.errorMessage = $localize`Port number must be between 1 and 65535`;
      this.errorFound = true;
    }

    if (!dataService.isNullOrEmpty(values.adminAgent.retryCounter) && !dataService.valueInRange(values.adminAgent.retryCounter, 0, 9999)) {
      this.errorMessage = $localize`Retry counter must be between 0 and 9999`;
      this.errorFound = true;
    }

    if (!dataService.isNullOrEmpty(values.adminAgent.retryHours) && !dataService.valueInRange(values.adminAgent.retryHours, 0, 99)) {
      this.errorMessage = $localize`Retry waiting delay hours must be between 0 and 99`;
      this.errorFound = true;
    }

    if (!dataService.isNullOrEmpty(values.adminAgent.retryMinutes) && !dataService.valueInRange(values.adminAgent.retryMinutes, 0, 99)) {
      this.errorMessage = $localize`Retry waiting delay minutes must be between 0 and 99`;
      this.errorFound = true;
    }

    if (!dataService.isNullOrEmpty(values.adminAgent.retrySeconds) && !dataService.valueInRange(values.adminAgent.retrySeconds, 0, 99)) {
      this.errorMessage = $localize`Retry waiting delay seconds must be between 0 and 99`;
      this.errorFound = true;
    }

    if (
      +values.adminAgent.dataDestinationType === 1 &&
      (dataService.isNullOrEmpty(values.adminAgent.dataDestinationAddress.ipv4.address1) ||
        dataService.isNullOrEmpty(values.adminAgent.dataDestinationAddress.ipv4.address2) ||
        dataService.isNullOrEmpty(values.adminAgent.dataDestinationAddress.ipv4.address3) ||
        dataService.isNullOrEmpty(values.adminAgent.dataDestinationAddress.ipv4.address4))
    ) {
      this.errorMessage = $localize`Invalid IPv4 address values, please ensure all address components are complete.`;
      this.errorFound = true;
    } else if (
      +values.adminAgent.dataDestinationType === 2 &&
      (!dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address1, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address2, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address3, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address4, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address5, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address6, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address7, 2, 2) ||
        !dataService.validHexValue(values.adminAgent.dataDestinationAddress.ipv6.address8, 2, 2))
    ) {
      this.errorMessage = $localize`Invalid IPv6 address values, please ensure all address components are complete.`;
      this.errorFound = true;
    }

    if (dataService.isNullOrEmpty(values.adminAgent.keyVersion)) {
      this.errorMessage = $localize`Key Version Number values must be entered`;
      this.errorFound = true;
    } else if (!dataService.hexValueInRange(values.adminAgent.keyVersion, 0x40, 0x4f)) {
      this.errorMessage = $localize`Key Version Number value must be between 40 and 4F`;
      this.errorFound = true;
    }

    return this.errorFound;
  }
}
